import { flexRender, type SortingState, type Table } from '@tanstack/react-table';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { TableHead, TableHeader, TableRow } from '@/_shared/components/ui/table';
import { cn } from '@/_shared/lib/utils';

import type { TableColumnMeta } from './types';
import { alignClass } from './utils';

export function TableHeaderRow<T>({
  table,
  sorting,
  virtualize,
  sticky = true,
}: {
  table: Table<T>;
  sorting: SortingState;
  virtualize: boolean;
  /** Pin the header while the body scrolls. False when the table virtualizes against an outer scroller. */
  sticky?: boolean;
}): React.ReactElement {
  return (
    <TableHeader>
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id} className="hover:bg-transparent" aria-rowindex={virtualize ? 1 : undefined}>
          {headerGroup.headers.map((header) => {
            const meta = header.column.columnDef.meta as TableColumnMeta | undefined;
            const canSort = header.column.getCanSort();
            const activeSort = sorting.find((s) => s.id === header.column.id);
            const sorted = activeSort ? (activeSort.desc ? 'desc' : 'asc') : false;
            const sortHandler = header.column.getToggleSortingHandler();

            return (
              <TableHead
                key={header.id}
                scope="col"
                aria-label={meta?.headerAriaLabel}
                aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined}
                tabIndex={canSort ? 0 : undefined}
                onClick={sortHandler}
                onKeyDown={
                  canSort
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          sortHandler?.(e);
                        }
                      }
                    : undefined
                }
                className={cn(
                  'border-b border-border bg-card',
                  // Pinning resolves against the nearest scroll container. With an external scroller that
                  // is the whole page, so the header would float over unrelated content — pin only when
                  // the table owns its viewport.
                  sticky && 'sticky top-0 z-10',
                  alignClass(meta?.align),
                  canSort &&
                    'cursor-pointer select-none hover:text-foreground focus:outline-none focus-visible:outline-2 focus-visible:outline-brand',
                )}
              >
                <span
                  className={cn(
                    'flex items-center gap-1',
                    meta?.align === 'right' && 'justify-end',
                    meta?.align === 'center' && 'justify-center',
                  )}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}

                  {canSort && (
                    <span className="flex flex-col" aria-hidden>
                      <ChevronUp
                        size={12}
                        strokeWidth={2.5}
                        className={sorted === 'asc' ? 'text-brand' : 'text-muted-foreground/40'}
                        data-testid={sorted === 'asc' ? 'sort-asc' : undefined}
                      />
                      <ChevronDown
                        size={12}
                        strokeWidth={2.5}
                        className={cn('-mt-1', sorted === 'desc' ? 'text-brand' : 'text-muted-foreground/40')}
                        data-testid={sorted === 'desc' ? 'sort-desc' : undefined}
                      />
                    </span>
                  )}
                </span>
              </TableHead>
            );
          })}
        </TableRow>
      ))}
    </TableHeader>
  );
}
